import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import confetti from "canvas-confetti";
import { Sparkles, Wand2, Palette, Target, Brain, Camera, Rocket } from "lucide-react";
import DuckyAvatar from "@/components/app/DuckyAvatar";

/**
 * Full-screen animated overlay while the AI cooks up the user's goal plan.
 * Cycles cinematic status messages, orbiting icons, animated progress bar,
 * and fires a confetti burst as it hands off. Optional `onDone` closes it.
 */
const STAGES = [
  { icon: Brain,   label: "Reading your goal…",           color: "#F59E0B" },
  { icon: Target,  label: "Picking your journey theme…",  color: "#DB2777" },
  { icon: Wand2,   label: "Coaching the AI strategist…",   color: "#7C3AED" },
  { icon: Palette, label: "Painting your dream in pixels…", color: "#059669" },
  { icon: Camera,  label: "Framing your vision…",          color: "#0EA5E9" },
  { icon: Sparkles,label: "Adding the gold flourishes…",   color: "#F59E0B" },
  { icon: Rocket,  label: "Almost ready to fly…",          color: "#EA580C" },
];

const QUACK_LINES = [
  "Ducky is fluffing his feathers…",
  "✨ Sprinkling a little Credit Quack magic…",
  "🎯 Aligning your funding stars…",
  "💰 Sizing up the smartest path…",
  "🚀 Prepping the launchpad…",
  "🏦 Whispering to the credit gods…",
  "📈 Charting your comeback…",
];

export default function GoalBuildingOverlay({ open, onDone, goalText = "" }) {
  const [stageIdx, setStageIdx] = useState(0);
  const [tickerIdx, setTickerIdx] = useState(0);
  const [progress, setProgress] = useState(4);
  const doneFiredRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setStageIdx(0); setTickerIdx(0); setProgress(4);
      doneFiredRef.current = false;
      return;
    }
    // Rotate stage every ~5s, ticker line every 2.2s
    const stageT = setInterval(() => setStageIdx((i) => (i + 1) % STAGES.length), 5000);
    const tickerT = setInterval(() => setTickerIdx((i) => (i + 1) % QUACK_LINES.length), 2200);
    // Progress creeps up asymptotically toward 92% until we get onDone
    const progT = setInterval(() => setProgress((p) => (p < 92 ? p + Math.max(0.4, (92 - p) * 0.02) : p)), 220);
    return () => { clearInterval(stageT); clearInterval(tickerT); clearInterval(progT); };
  }, [open]);

  // Fire confetti + fill bar once onDone becomes true (we let parent unmount us)
  useEffect(() => {
    if (open && !doneFiredRef.current && onDone === "READY") {
      doneFiredRef.current = true;
      setProgress(100);
      const defaults = { spread: 90, ticks: 80, gravity: 0.85, colors: ["#F59E0B","#FBBF24","#FEF3C7","#EAB308","#FFFFFF"] };
      confetti({ ...defaults, particleCount: 80, origin: { x: 0.2, y: 0.6 } });
      confetti({ ...defaults, particleCount: 80, origin: { x: 0.8, y: 0.6 } });
      setTimeout(() => confetti({ ...defaults, particleCount: 120, origin: { x: 0.5, y: 0.55 } }), 200);
    }
  }, [onDone, open]);

  if (!open) return null;

  const Stage = STAGES[stageIdx];
  const StageIcon = Stage.icon;

  return createPortal(
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/85 backdrop-blur-md p-6" data-testid="goal-building-overlay">
      {/* Ambient particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 30 }).map((_, i) => (
          <span
            key={i}
            className="absolute rounded-full opacity-60"
            style={{
              left: `${(i * 173) % 100}%`,
              top: `${(i * 91) % 100}%`,
              width: `${(i % 4) + 3}px`,
              height: `${(i % 4) + 3}px`,
              background: i % 3 === 0 ? "#FBBF24" : i % 3 === 1 ? "#F59E0B" : "#FFFFFF",
              animation: `cq-float-up ${8 + (i % 6)}s linear ${i * -0.4}s infinite`,
            }}
          />
        ))}
      </div>

      <div className="relative w-full max-w-lg text-center">
        {/* Orbiting halo */}
        <div className="relative mx-auto h-52 w-52">
          <div className="absolute inset-0 rounded-full border-2 border-amber-500/30 animate-[cq-spin-slow_10s_linear_infinite]"
               style={{ boxShadow: "0 0 60px 4px rgba(245,158,11,0.3)" }} />
          <div className="absolute inset-3 rounded-full border-2 border-dashed border-amber-300/40 animate-[cq-spin-reverse_14s_linear_infinite]" />
          {/* Rotating quack orbit */}
          {[0, 72, 144, 216, 288].map((deg, i) => (
            <div
              key={deg}
              className="absolute top-1/2 left-1/2 h-3 w-3 rounded-full animate-[cq-spin-slow_10s_linear_infinite]"
              style={{
                transform: `translate(-50%,-50%) rotate(${deg}deg) translateY(-104px)`,
                transformOrigin: "center center",
                background: STAGES[i % STAGES.length].color,
                boxShadow: `0 0 12px ${STAGES[i % STAGES.length].color}`,
              }}
            />
          ))}
          {/* Center orb */}
          <div className="absolute inset-1/4 rounded-full flex items-center justify-center animate-[cq-pulse_1.8s_ease-in-out_infinite]"
               style={{
                 background: `radial-gradient(circle, ${Stage.color} 0%, #0f172a 70%)`,
                 boxShadow: `0 0 40px 8px ${Stage.color}55`,
               }}>
            <span className="select-none animate-[cq-bob_2.2s_ease-in-out_infinite]">
              <DuckyAvatar size={64} ring={false} bare />
            </span>
          </div>
        </div>

        {/* Stage headline */}
        <p className="mt-6 text-[10px] uppercase tracking-[0.32em] font-black text-amber-300">
          Credit Quack AI · Building your journey
        </p>
        <h2 className="font-display text-3xl sm:text-4xl font-black text-white mt-2 leading-tight min-h-[3.5rem] flex items-center justify-center gap-2">
          <StageIcon size={22} style={{ color: Stage.color }} />
          {Stage.label}
        </h2>
        {goalText && (
          <p className="mt-1 text-slate-400 text-sm italic max-w-md mx-auto line-clamp-2">"{goalText}"</p>
        )}

        {/* Ticker line */}
        <p className="mt-4 text-sm text-amber-200/90 font-bold animate-[cq-fade_2.2s_ease-in-out_infinite]" key={tickerIdx}>
          {QUACK_LINES[tickerIdx]}
        </p>

        {/* Progress bar */}
        <div className="mt-6 mx-auto max-w-sm">
          <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                width: `${progress}%`,
                background: "linear-gradient(90deg,#F59E0B,#FBBF24,#F59E0B)",
                backgroundSize: "200% 100%",
                animation: "cq-shimmer-bar 2s linear infinite",
              }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-slate-500">
            <span>Cooking your plan</span>
            <span className="text-amber-300">{Math.min(100, Math.round(progress))}%</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes cq-spin-slow { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes cq-spin-reverse { from{transform:rotate(0)} to{transform:rotate(-360deg)} }
        @keyframes cq-pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.08)} }
        @keyframes cq-bob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes cq-fade { 0%,100%{opacity:.55} 50%{opacity:1} }
        @keyframes cq-shimmer-bar { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes cq-float-up { 0%{transform:translateY(0);opacity:.6} 100%{transform:translateY(-120vh);opacity:0} }
      `}</style>
    </div>,
    document.body
  );
}
